/*
 * Copyright (c) 2020 Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { Formik, Form, FieldArray } from "formik";
import Sortable from "react-sortablejs";
import classNames from "classnames";
import { uniq } from "lodash";

// @material-ui/core components
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import {
  Badge,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";

import { submitExButton } from "components/Form/FormField";
import {
  TestActionParams,
  TestResult,
  reportCorrect,
  reportWrong,
  reportFeedback,
  showHtmlText,
} from "./ExMain";
import { sAnsShowAbove, sRightAns } from "assets/jss/views/ResStrings";
import { testType } from "client/reducers/infoboxReducer";
import { progressCircles } from "components/Form/FormField";

import exerciseStyle from "assets/jss/views/exercise/exerciseStyle.jsx";
const styles = createStyles(exerciseStyle);

interface Props extends WithStyles<typeof styles> {
  classes: any;
  exercise: any;
  testResult: TestResult;
  quit: boolean;
  onAction: (params: TestActionParams) => void;
}

const ExSortableBase: React.FC<Props> = ({
  classes,
  exercise,
  testResult,
  quit,
  onAction,
}: Props) => {
  const [s_rand, setRandom] = useState<Array<number>>([]);
  const [s_report, setReport] = useState<React.ReactNode>(null);

  // for Categorize (move the IDs, instead of whole data, in <Sortable />)
  const [sourceIDs, setSourceIDs] = useState<number[]>([]);
  const [targetIDs, setTargetIDs] = useState<Array<number[]>>([[]]);

  const idMatchLeft = "exMatchLX";

  useEffect(() => {
    if (exercise) {
      let rand;
      if (testResult) {
        // testResult could be {} due to incompleted test, fill the missed userAnsNum, rand
        rand = testResult.rand || exercise.e_ans.map((x: any, i: number) => i);
        if (exercise.type !== testType.CATEGORIZE) {
          setReport(
            reOrderMatchReport(testResult.userAnsNum || exercise.e_ans, rand),
          );
        }
      } else {
        rand = exercise.e_text.map((t: string, idx: number) => idx);
        rand.sort(() => Number(Math.random() - 0.5));
      }
      setRandom(rand);

      if (exercise.type === testType.CATEGORIZE) {
        if (testResult) {
          // testResult could be {} due to incompleted test, fill the missed userAnsNum
          setSourceIDs([]);
          const targets: Array<number[]> = exercise.aux.map(() => []);
          (
            testResult.userAnsNum || exercise.e_ans
          ).forEach((ua: number, idx: number) => targets[ua].push(idx));
          setTargetIDs(targets);
          setReport(categorzieReport(testResult.userAnsNum || exercise.e_ans));
        } else {
          setSourceIDs(rand);
          setTargetIDs([[]]);
        }
      }
    }
  }, [exercise]);

  useEffect(() => {
    if (quit) {
      onAction({ action: "quit", data: {} });
    }
  }, [quit]);

  const renderItemRight = (entry: number, index: number) => {
    const correct = s_rand[entry] === index;

    return (
      <div
        className={classes.itemPart}
        key={index}
        style={{
          display: "flex",
          alignItems: "center",
          cursor: testResult ? "default" : "grab",
        }}
        data-id={entry}>
        {!testResult && (
          <i
            className={classNames({
              [classes.grabGreen]: true,
              ["far fa-hand-paper medium"]: true,
            })}
          />
        )}
        <div className={classes.itemRight} style={{ marginLeft: 0 }}>
          <Badge
            invisible={!(testResult && testResult.userAnsNum)}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            badgeContent={correct ? <>&#x2713;</> : "X"}
            color={correct ? "secondary" : "error"}>
            &nbsp;{showHtmlText(exercise.e_text[s_rand[entry]])}
          </Badge>
        </div>
      </div>
    );
  };

  const renderTestEntries = (values: any) => {
    // if values is null, it's called by Reorder report generator to render correct answer
    const ans = values
      ? values.userAns
      : exercise.e_text.map((x: any, i: number) => i); // correct answer: [0, 1, 2...]

    return ans.map((entry: number, index: number) =>
      values ? (
        <div
          key={index}
          style={{ width: exercise.type === testType.MATCH ? "100%" : "80%" }}>
          {renderItemRight(entry, index)}
        </div>
      ) : (
        <li value={index + 1} key={index}>
          <div
            className={classes.itemRight}
            style={{ marginLeft: 0, width: "79.5%" }}>
            &nbsp;{showHtmlText(exercise.e_text[entry])}
          </div>
        </li>
      ),
    );
  };

  const renderTestBody = (values: any) => {
    switch (exercise.type) {
      case testType.MATCH:
        return testResult ? (
          renderMatchResult(values)
        ) : (
          <>
            {renderMatchAsTable(values.userAns, true)}
            {renderMatchAsTable(values.userAns, false)}
          </>
        );

      case testType.CATEGORIZE:
        return renderCategorize();

      case testType.REORDER:
        return renderSortableFields(renderTestEntries(values));

      default:
        return "";
    }
  };

  const renderSortableFields = (body: any) => (
    <FieldArray
      name={"userAns"}
      render={(arrayHelpers) => (
        <Sortable
          tag={exercise.type === testType.MATCH ? "tbody" : "div"}
          options={{ disabled: testResult ? true : false }}
          onChange={(order: any, sortable: any, evt: any) =>
            arrayHelpers.move(evt.oldIndex, evt.newIndex)
          }>
          {body}
        </Sortable>
      )}
    />
  );

  const sortableOption = {
    animation: 150,
    sort: true,
    group: {
      name: "catego",
      pull: true,
      put: true,
    },
    disabled: testResult ? true : false,
  };

  const renderTargets = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
      {exercise.aux.map((label: string, index: number) => (
        <div key={index} className={classes.targetGroup}>
          <div className={classes.testGroup}>{showHtmlText(label)}:</div>
          <Sortable
            options={sortableOption}
            tag="div"
            onChange={(itemsOrg: any[]) => {
              let items = itemsOrg.map((i) => Number(i));
              items = uniq(items); // Remove duplicate items
              let x: Array<number[]> = targetIDs.slice(); // clone array
              x[index] = items;
              setTargetIDs(x);
            }}>
            {(targetIDs[index] || []).map((item, idx) => (
              <div
                key={idx}
                data-id={item}
                className={classes.itemCat}
                style={{ cursor: testResult ? "default" : "grab" }}>
                {showHtmlText(exercise.e_text[item])}
              </div>
            ))}
          </Sortable>
        </div>
      ))}
    </div>
  );

  const renderCategorize = () => (
    <div style={{ display: "flex" }}>
      <div className={classes.sourceGroup}>
        <div className={classes.testGroup}>Items to be categorized:</div>
        <Sortable
          options={sortableOption}
          tag="div"
          onChange={(items: number[]) => setSourceIDs(items)}>
          {sourceIDs.map((item, index) => (
            <div
              key={index}
              data-id={item}
              className={classes.itemCat}
              style={{ cursor: testResult ? "default" : "grab" }}>
              {showHtmlText(exercise.e_text[item])}
            </div>
          ))}
        </Sortable>
      </div>
      {renderTargets()}
    </div>
  );

  const renderMatchItemLeft = (index: number, item: string) => (
    <div
      key={index}
      className={classes.itemPart}
      style={
        testResult
          ? {
              paddingBottom: "0.5rem",
              borderBottom: "1px solid gray",
              margin: "0.5rem 0 1rem",
            }
          : {}
      }>
      {showHtmlText(item)}
    </div>
  );

  const renderMatchAsTable = (ans: number[], left: boolean) => {
    // Render the Match test (not result) as a table, twice.
    // The first time draws a table with static data, non-moveable; right side is hidden
    // The 2dn time draws a table with sortable entries, left side hidden. Move this drawing up to cover the 1st drawing.
    // With this, we use <Table> to align left/side row height; and right side is moveable.
    let height;
    if (left === false) {
      const tab = document.getElementById(idMatchLeft);
      if (!tab) return progressCircles();
      height = getComputedStyle(tab).height;
    }

    const body = exercise.aux.map((item: string, index: number) => {
      const w = { width: "50%" };
      const hide = { ...w, visibility: "hidden" };
      return (
        <TableRow key={index}>
          <TableCell style={left ? w : hide} align="left">
            {renderMatchItemLeft(index, item)}
          </TableCell>
          <TableCell style={left ? hide : w} align="right">
            {renderItemRight(ans[index], index)}
          </TableCell>
        </TableRow>
      );
    });
    return left ? (
      <div id={left ? idMatchLeft : "x"}>
        <Table>
          <TableBody>{body}</TableBody>
        </Table>
      </div>
    ) : (
      <div style={{ position: "relative", top: `-${height}` }}>
        <Table>{renderSortableFields(body)}</Table>
      </div>
    );
  };

  const renderMatchResult = (values: any) =>
    values.userAns.map((entry: number, index: number) => {
      return (
        <div key={index} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "50%" }}>
            {renderMatchItemLeft(index, exercise.aux[index])}
          </div>
          <div style={{ width: "50%" }}>{renderItemRight(entry, index)}</div>
        </div>
      );
    });

  const renderOrderMatchAnsEntry = (i: number) =>
    exercise.type === testType.MATCH ? (
      <div key={i} style={{ display: "flex", alignItems: "center" }}>
        <div style={{ minWidth: "50%", maxWidth: "50%" }}>
          {renderMatchItemLeft(i, exercise.aux[i])}
        </div>
        <span className={classes.itemRight}>
          &nbsp;{showHtmlText(exercise.e_text[i])}
        </span>
      </div>
    ) : (
      <li key={i} value={i + 1} className={classes.itemPart}>
        {showHtmlText(exercise.e_text[i])}
      </li>
    );

  const reportMsg = (isCorrect: boolean) => {
    if (isCorrect) {
      return testResult.userAnsNum
        ? reportCorrect(classes)
        : reportWrong(classes, true, sAnsShowAbove);
    }
    return reportWrong(classes, false);
  };

  const reportBody = (
    isCorrect: boolean,
    correctAns: React.ReactNode,
    feedbkList: React.ReactNode,
    showFeedback: boolean,
  ) => (
    <>
      {reportMsg(isCorrect)}
      {!isCorrect && (
        <>
          <div className={classes.reportCorrect}>{sRightAns}</div>
          {correctAns}
        </>
      )}
      {showFeedback &&
        reportFeedback(feedbkList, exercise.explain, exercise.fbk, classes)}
    </>
  );

  const reOrderMatchReport = (userAns: number[], rand: number[]) => {
    const { e_ans } = exercise;
    let isCorrect: boolean;

    // user answer viewed from DB
    const userAns2 = e_ans.map((ans: number, i: number) => rand[userAns[i]]);
    isCorrect = !userAns2.some((a: number, i: number) => a !== i); // should be simply 0, 1, 2, 3...

    let correctAnsList: Array<React.ReactNode> = [];
    let idx: number;
    if (!isCorrect) {
      if (exercise.type === testType.MATCH) {
        for (idx = 0; idx < exercise.e_text.length; idx++) {
          correctAnsList.push(renderOrderMatchAnsEntry(idx));
        }
      } else {
        correctAnsList = renderTestEntries(null);
      }
    }

    let feedbackList: Array<React.ReactNode> = [];
    exercise.e_feedback.forEach((feedbk: string, idx: number) => {
      if (feedbk !== "")
        feedbackList.push(
          <li
            key={idx}
            value={exercise.type === testType.REORDER ? idx + 1 : 0}>
            {showHtmlText(feedbk, classes)}
          </li>,
        );
    });
    const feedback: React.ReactNode = feedbackList.length ? (
      exercise.type === testType.REORDER ? (
        <ol>{feedbackList}</ol>
      ) : (
        <ul>{feedbackList}</ul>
      )
    ) : null;

    return reportBody(
      isCorrect,
      exercise.type === testType.MATCH ? (
        <>{correctAnsList}</>
      ) : (
        <ol>{correctAnsList}</ol>
      ),
      feedback,
      feedbackList.length !== 0 || exercise.explain !== "",
    );
  };

  const categorzieReport = (userAns: number[]) => {
    const { e_text, e_ans, aux } = exercise;
    let isCorrect: boolean = true;

    let correctAnsList: Array<React.ReactNode> = [];
    let feedbackList: Array<React.ReactNode> = [];
    userAns.forEach((ua, i) => {
      if (ua !== e_ans[i]) {
        correctAnsList.push(
          <li key={i} className={classes.itemPart}>
            {showHtmlText(
              `“<u>${e_text[i]}</u>” should be in group “<u>${
                aux[e_ans[i]]
              }</u>”`,
              classes,
            )}
          </li>,
        );
        isCorrect = false;
      }
      const feedbk = exercise.e_feedback[i];
      if (feedbk !== "")
        feedbackList.push(
          <li key={i} className={classes.itemPart}>
            {showHtmlText(feedbk, classes)}
          </li>,
        );
    });

    return reportBody(
      isCorrect,
      <ul>{correctAnsList}</ul>,
      feedbackList.length ? <ul>{feedbackList}</ul> : null,
      feedbackList.length !== 0 || exercise.explain !== "",
    );
  };

  const formInitVaules = () => {
    if (exercise.type === testType.CATEGORIZE) {
      const { e_text, aux, e_ans } = exercise;
      return {
        e_text: e_text || [],
        e_ans: e_ans || [],
        aux: aux || [],
      };
    }

    // ReOrder, Match
    return {
      userAns:
        testResult && testResult.userAnsNum
          ? testResult.userAnsNum
          : exercise.e_text.map((e: string, i: number) => i),
    };
  };

  const submitSortable = () => {
    const btn = submitExButton(
      "Next",
      exercise.type === testType.CATEGORIZE && sourceIDs.length !== 0,
    );
    if (exercise.type !== testType.MATCH) return btn;

    const tab = document.getElementById(idMatchLeft);
    return (
      tab !== null && (
        <div
          style={{
            position: "relative",
            top: `-${getComputedStyle(tab).height}`,
          }}>
          {btn}
        </div>
      )
    );
  };

  return (
    <div className={classes.exContainer}>
      {showHtmlText(exercise.statement, classes)}
      <Formik
        initialValues={formInitVaules()}
        enableReinitialize={true}
        onSubmit={(values, formikBag) => {
          const { e_ans } = exercise;
          if (exercise.type === testType.CATEGORIZE) {
            const userAns: number[] = [];
            formikBag.resetForm({ userAns });

            targetIDs.forEach((group: any, i: number) =>
              group.forEach((j: number) => (userAns[j] = i)),
            );
            onAction({
              action: "next",
              data: {
                userAnsNum: userAns,
                correct: !userAns.some((ua, i) => ua !== e_ans[i]),
              },
            });
          } else {
            formikBag.resetForm({
              userAns: exercise.e_text.map((e: string, i: number) => i),
            });

            // user answer viewed from DB
            const userAns2 = e_ans.map(
              (x: any, i: number) => s_rand[values.userAns[i]],
            );
            onAction({
              action: "next",
              data: {
                rand: s_rand,
                userAnsNum: values.userAns,
                correct: !userAns2.some((a: number, i: number) => a !== i), // should be simply 0, 1, 2, 3... },
              },
            });
          }
        }}
        render={({ values }) => {
          return (
            <Form>
              {s_rand.length && renderTestBody(values)}
              {!testResult && submitSortable()}
              {s_report}
            </Form>
          );
        }}
      />
    </div>
  );
};

export const ExSortable = withStyles(styles)(ExSortableBase);
